import React, { useState, useEffect } from 'react';
import { Save, Plus, Trash2, Eye, EyeOff, Brain, Sparkles } from 'lucide-react';
import Modal from '../components/common/Modal';
import CustomSelect from '../components/common/CustomSelect';
import NotificationSystem, { useNotification } from '../components/common/NotificationSystem';
import settingsService from '../services/settingsService';

const SettingsPage = () => {
  const { notification, showNotification, hideNotification } = useNotification();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingAI, setTestingAI] = useState(false);
  const [savingProposal, setSavingProposal] = useState(false);

  // AI Config
  const [apiKey, setApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [geminiModel, setGeminiModel] = useState('gemini-2.0-flash');
  const [temperature, setTemperature] = useState(0.7);
  const [hasExistingKey, setHasExistingKey] = useState(false);
  const [maskedKey, setMaskedKey] = useState('');

  // Proposal Config
  const [systemPrompt, setSystemPrompt] = useState('');
  const [hourlyRate, setHourlyRate] = useState(19000);
  const [currency, setCurrency] = useState('COP');

  // Project Config
  const [hoursPerDay, setHoursPerDay] = useState(6);
  const [excludeWeekends, setExcludeWeekends] = useState(true);

  // Knowledge Base
  const [knowledgeEntries, setKnowledgeEntries] = useState([]);
  const [showAddKBModal, setShowAddKBModal] = useState(false);
  const [kbForm, setKbForm] = useState({ title: '', content: '', allowedAreas: ['general'] });
  const [showDeleteKBModal, setShowDeleteKBModal] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await settingsService.getSettings();
      const s = data.settings;
      setGeminiModel(s.ai?.geminiModel || 'gemini-2.0-flash');
      setTemperature(s.ai?.temperature || 0.7);
      setHasExistingKey(s.ai?.hasApiKey || false);
      setMaskedKey(s.ai?.geminiApiKeyMasked || '');
      setKnowledgeEntries(s.knowledgeBase?.entries || []);
      if (s.proposalConfig) {
        setSystemPrompt(s.proposalConfig.systemPrompt || '');
        setHourlyRate(s.proposalConfig.hourlyRate || 19000);
        setCurrency(s.proposalConfig.currency || 'COP');
      }
      if (s.projectConfig) {
        setHoursPerDay(s.projectConfig.hoursPerDay || 6);
        setExcludeWeekends(s.projectConfig.excludeWeekends ?? true);
      }
    } catch (error) {
      showNotification('Error al cargar la configuración', 'error');
    } finally { setLoading(false); }
  };

  const handleSaveAI = async () => {
    try {
      setSaving(true);
      await settingsService.updateAISettings({
        geminiApiKey: apiKey || undefined,
        geminiModel,
        temperature
      });
      showNotification('Configuración de IA guardada exitosamente', 'success');
      setApiKey('');
      loadSettings();
    } catch (error) {
      showNotification(error.message, 'error');
    } finally { setSaving(false); }
  };

  const handleTestAI = async () => {
    try {
      setTestingAI(true);
      const data = await settingsService.testAISettings({
        geminiApiKey: apiKey || undefined,
        geminiModel
      });
      showNotification(data.message || '¡Conexión exitosa con la IA!', 'success');
    } catch (error) {
      showNotification(error.message, 'error');
    } finally {
      setTestingAI(false);
    }
  };

  const handleSaveProposalConfig = async () => {
    try {
      setSavingProposal(true);
      await settingsService.updateProposalConfig({ systemPrompt, hourlyRate, currency });
      await settingsService.updateProjectConfig({ hoursPerDay, excludeWeekends });
      showNotification('Configuraciones operativas guardadas exitosamente', 'success');
    } catch (error) {
      showNotification(error.message, 'error');
    } finally { setSavingProposal(false); }
  };

  const handleAddKB = async (e) => {
    e.preventDefault();
    try {
      const data = await settingsService.addKnowledgeEntry(kbForm);
      setKnowledgeEntries(data.entries || []);
      showNotification(data.message, 'success');
      setShowAddKBModal(false);
      setKbForm({ title: '', content: '', allowedAreas: ['general'] });
    } catch (error) {
      showNotification(error.message, 'error');
    }
  };

  const handleDeleteKB = async () => {
    if (!entryToDelete) return;
    try {
      const data = await settingsService.deleteKnowledgeEntry(entryToDelete._id);
      setKnowledgeEntries(data.entries || []);
      showNotification(data.message, 'success');
      setShowDeleteKBModal(false);
      setEntryToDelete(null);
    } catch (error) {
      showNotification(error.message, 'error');
    }
  };

  const toggleKBArea = (area) => {
    setKbForm(prev => ({
      ...prev,
      allowedAreas: prev.allowedAreas.includes(area)
        ? prev.allowedAreas.filter(a => a !== area)
        : [...prev.allowedAreas, area]
    }));
  };

  const areaLabels = {
    general: { label: 'General', color: '#6b7280' },
    desarrollo: { label: 'Desarrollo', color: '#2563eb' },
    comercial: { label: 'Comercial', color: '#16a34a' },
    financiero: { label: 'Financiero', color: '#d97706' }
  };

  if (loading) {
    return <div className="flex flex-col items-center justify-center py-12"><div className="loading-spinner"></div><p className="mt-4 text-gray-600">Cargando configuración...</p></div>;
  }

  return (
    <div className="animate-fadeIn">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">⚙️ Configuración</h1>
        <p className="text-gray-600">Configuración general de la plataforma RxApp • Solo Super Administradores</p>
      </div>

      {/* ═══════════ SECCIÓN 1: CONFIGURACIÓN DE IA ═══════════ */}
      <div className="card mb-6">
        <div className="card-header" style={{ padding: '16px', borderBottom: '1px solid #e5e7eb' }}>
          <h2 className="text-lg font-semibold text-gray-900">🧠 Configuración de Inteligencia Artificial</h2>
        </div>
        <div className="card-content" style={{ padding: '20px' }}>
          {/* API Key */}
          <div className="form-group" style={{ marginBottom: '20px' }}>
            <label className="form-label">API Key de Gemini</label>
            {hasExistingKey && (
              <div style={{ marginBottom: '8px', padding: '8px 12px', backgroundColor: '#dcfce7', borderRadius: '8px', fontSize: '13px', color: '#16a34a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>✅ API Key configurada:</span>
                <code style={{ fontFamily: 'monospace', letterSpacing: '1px' }}>{maskedKey}</code>
              </div>
            )}
            <div style={{ display: 'flex', gap: '8px' }}>
              <div style={{ flex: 1, position: 'relative' }}>
                <input
                  type={showApiKey ? 'text' : 'password'}
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  className="form-input"
                  placeholder={hasExistingKey ? 'Escribe una nueva key para reemplazar...' : 'Pega tu API Key de Google AI Studio aquí'}
                />
                <button
                  type="button"
                  onClick={() => setShowApiKey(!showApiKey)}
                  style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}
                >
                  {showApiKey ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <p className="text-sm text-gray-400 mt-1">Obtén tu API Key en <a href="https://aistudio.google.com/apikey" target="_blank" rel="noreferrer" style={{ color: '#4f7942' }}>Google AI Studio</a></p>
          </div>

          {/* Modelo + Temperatura */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            <CustomSelect
              label="Modelo de IA"
              value={geminiModel}
              onChange={(v) => setGeminiModel(v)}
              options={[
                { value: 'gemini-3.7-flash', label: '🚀 Gemini 3.7 Flash (Recomendado - Máximo Rendimiento)' },
                { value: 'gemini-3.6-flash', label: '⚡ Gemini 3.6 Flash (Rápido y Potente)' },
                { value: 'gemini-flash-lite-latest', label: '⚡ Gemini Flash-Lite Latest (Siempre Actualizado)' },
                { value: 'gemini-3.5-flash-lite', label: '🔹 Gemini 3.5 Flash-Lite (Ultra Rápido)' },
                { value: 'gemini-3.1-flash-lite', label: '🔹 Gemini 3.1 Flash-Lite (Eficiente)' },
                { value: 'gemini-3-flash-preview', label: '🧪 Gemini 3 Flash Preview' }
              ]}
            />
            <div className="form-group">
              <label className="form-label">Temperatura de Creatividad: {temperature}</label>
              <input
                type="range" min="0" max="1" step="0.1"
                value={temperature}
                onChange={(e) => setTemperature(parseFloat(e.target.value))}
                style={{ width: '100%', accentColor: '#4f7942' }}
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>Preciso</span>
                <span>Creativo</span>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
            <button onClick={handleSaveAI} disabled={saving || testingAI} className="btn btn-primary">
              <Save size={16} /> {saving ? 'Guardando...' : 'Guardar Configuración IA'}
            </button>
            <button
              type="button"
              onClick={handleTestAI}
              disabled={saving || testingAI}
              className="btn"
              style={{
                backgroundColor: '#10b981',
                color: '#ffffff',
                border: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                borderRadius: '6px',
                fontWeight: '500',
                cursor: (saving || testingAI) ? 'not-allowed' : 'pointer',
                opacity: (saving || testingAI) ? 0.7 : 1
              }}
            >
              <Sparkles size={16} /> {testingAI ? 'Probando...' : 'Probar Conexión'}
            </button>
          </div>
        </div>
      </div>

      {/* ═══════════ SECCIÓN 2: CONFIGURACIÓN DE PROPUESTAS ═══════════ */}
      <div className="card mb-6">
        <div className="card-header" style={{ padding: '16px', borderBottom: '1px solid #e5e7eb' }}>
          <h2 className="text-lg font-semibold text-gray-900">📝 Configuración de Propuestas y Dimensionamiento</h2>
          <p className="text-sm text-gray-500 mt-1">Define el comportamiento completo de la IA al generar levantamientos, dimensionamientos y propuestas</p>
        </div>
        <div className="card-content" style={{ padding: '20px' }}>
          {/* Campos rápidos */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '20px' }}>
            <div className="form-group" style={{ gridColumn: 'span 2' }}>
              <label className="form-label">💰 Tarifa Base por Hora</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input type="number" value={hourlyRate} onChange={(e) => setHourlyRate(parseFloat(e.target.value) || 0)} className="form-input" min="0" step="500" style={{ flex: 1 }} />
                <input type="text" value={currency} onChange={(e) => setCurrency(e.target.value)} className="form-input" style={{ width: '80px' }} placeholder="COP" />
              </div>
              <p className="text-xs text-gray-400 mt-1">Valor base de referencia para dimensionamiento.</p>
            </div>
            
            <div className="form-group" style={{ gridColumn: 'span 1' }}>
              <label className="form-label">⏱ Horas Laborales/Día</label>
              <input type="number" value={hoursPerDay} onChange={(e) => setHoursPerDay(parseInt(e.target.value) || 6)} className="form-input" min="1" max="24" />
              <p className="text-xs text-gray-400 mt-1">Para cálculo automático de fechas cronograma.</p>
            </div>

            <div className="form-group flex flex-col justify-center" style={{ gridColumn: 'span 1', paddingTop: '22px' }}>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={excludeWeekends} onChange={(e) => setExcludeWeekends(e.target.checked)} className="form-checkbox h-5 w-5 text-green-600 rounded" />
                <span className="text-sm font-medium text-gray-700">Excluir fin de semana</span>
              </label>
              <p className="text-xs text-gray-400 mt-1 ml-7">Sábados y Domingos.</p>
            </div>
          </div>

          <div className="form-group" style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
            <div style={{ padding: '12px 16px', backgroundColor: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0', width: '100%' }}>
              <p className="text-sm text-green-800"><strong>💡 Tip:</strong> El prompt de abajo es la "mente" de tu IA. Define ahí todo: rol, fórmulas de precios, templates de documentos, tono, reglas de negocio.</p>
            </div>
          </div>

          {/* Prompt principal */}
          <div className="form-group" style={{ marginBottom: '20px' }}>
            <label className="form-label" style={{ fontSize: '15px' }}>📋 Prompt de Sistema para Generación de Propuestas</label>
            <textarea 
              value={systemPrompt} 
              onChange={(e) => setSystemPrompt(e.target.value)} 
              className="form-input form-textarea" 
              rows="20"
              placeholder={"Aquí defines TODO el comportamiento de la IA al generar propuestas.\n\nEjemplo de lo que puedes incluir:\n\n# ROL Y OBJETIVO PRINCIPAL\nActuarás como el Consultor Tecnológico y Comercial Senior de la agencia...\n\n# ESTRATEGIA DE PRECIOS\nTarifa base: $19.000 COP por hora.\nMargen estratégico: 40%...\n\n# FORMATOS DE DOCUMENTOS\n### Levantamiento de Requerimientos\n(template completo...)\n\n### Dimensionamiento Técnico\n(template completo...)\n\n### Propuesta Comercial\n(template completo...)\n\n# REGLAS DE NEGOCIO\n- Siempre incluir fase de QA\n- Condiciones de pago: 50-50\n- Validez: 15 días\n\n# TONO E INSTRUCCIONES\nMantén un tono consultivo y profesional..."}
              style={{ 
                fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                fontSize: '13px',
                lineHeight: '1.6',
                resize: 'vertical',
                minHeight: '400px'
              }}
            />
            <div className="flex justify-between items-center mt-2">
              <p className="text-xs text-gray-400">Este prompt se inyecta como instrucción del sistema cuando la IA genera propuestas. Si está vacío, se usa un prompt básico por defecto.</p>
              <span className="text-xs text-gray-400">{systemPrompt.length.toLocaleString()} caracteres</span>
            </div>
          </div>

          <button onClick={handleSaveProposalConfig} disabled={savingProposal} className="btn btn-primary">
            <Save size={16} /> {savingProposal ? 'Guardando...' : 'Guardar Configuración de Propuestas'}
          </button>
        </div>
      </div>

      {/* ═══════════ SECCIÓN 3: BASE DE CONOCIMIENTO ═══════════ */}
      <div className="card mb-6">
        <div className="card-header" style={{ padding: '16px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">📚 Base de Conocimiento Corporativo</h2>
            <p className="text-sm text-gray-500 mt-1">Alimenta a Xtreme-AI con información de tu empresa segmentada por área</p>
          </div>
          <button onClick={() => setShowAddKBModal(true)} className="btn btn-primary btn-sm">
            <Plus size={16} /> Agregar
          </button>
        </div>
        <div style={{ padding: '0' }}>
          {knowledgeEntries.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Brain size={40} className="mx-auto mb-3" />
              <p>No hay entradas en la base de conocimiento</p>
              <p className="text-sm mt-1">Agrega documentos o textos para que Xtreme-AI aprenda sobre tu empresa</p>
            </div>
          ) : (
            knowledgeEntries.map((entry, i) => (
              <div key={entry._id || i} style={{ padding: '16px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-gray-900">{entry.title}</span>
                    {!entry.isActive && <span className="badge badge-secondary" style={{ fontSize: '10px' }}>Inactiva</span>}
                  </div>
                  <p className="text-sm text-gray-500" style={{ 
                    maxWidth: '600px', display: '-webkit-box', WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical', overflow: 'hidden'
                  }}>{entry.content}</p>
                  <div className="flex gap-1 mt-2">
                    {entry.allowedAreas?.map((area, j) => (
                      <span key={j} style={{
                        padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600,
                        backgroundColor: areaLabels[area]?.color + '15', color: areaLabels[area]?.color
                      }}>{areaLabels[area]?.label || area}</span>
                    ))}
                  </div>
                </div>
                <button onClick={() => { setEntryToDelete(entry); setShowDeleteKBModal(true); }} className="btn btn-danger btn-sm">
                  <Trash2 size={14} />
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Info Box */}
      <div style={{ backgroundColor: '#eff6ff', border: '1px solid #93c5fd', borderRadius: '8px', padding: '16px' }}>
        <p className="text-sm text-blue-800">
          <strong>💡 ¿Cómo funciona todo junto?</strong><br />
          • <strong>Configuración de IA</strong>: Define qué modelo y API Key usa toda la plataforma.<br />
          • <strong>Prompt de Propuestas</strong>: Es la "mente" de la IA al generar documentos. Ahí defines rol, fórmulas, templates, tono y reglas de negocio.<br />
          • <strong>Base de Conocimiento</strong>: Alimenta a Xtreme-AI (chat global) con información corporativa segmentada por área y rol.
        </p>
      </div>

      {/* Modal Agregar KB */}
      <Modal isOpen={showAddKBModal} onClose={() => setShowAddKBModal(false)} title="📚 Agregar Conocimiento" onSubmit={handleAddKB} submitText="Agregar">
        <div className="form-section">
          <div className="form-group">
            <label className="form-label">Título <span className="label-required">*</span></label>
            <input type="text" value={kbForm.title} onChange={(e) => setKbForm(p => ({ ...p, title: e.target.value }))} className="form-input" placeholder="Ej. Política de precios 2025" required />
          </div>
          <div className="form-group" style={{ marginTop: '12px' }}>
            <label className="form-label">Contenido <span className="label-required">*</span></label>
            <textarea value={kbForm.content} onChange={(e) => setKbForm(p => ({ ...p, content: e.target.value }))} className="form-input form-textarea" rows="6"
              placeholder="Pega aquí documentos, políticas, información corporativa, procedimientos, etc."
              required
            />
          </div>
          <div className="form-group" style={{ marginTop: '12px' }}>
            <label className="form-label">Áreas con Acceso</label>
            <div className="flex flex-wrap gap-2 mt-1">
              {Object.entries(areaLabels).map(([area, info]) => (
                <button key={area} type="button" onClick={() => toggleKBArea(area)} style={{
                  padding: '6px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: 600,
                  border: `2px solid ${info.color}`, cursor: 'pointer',
                  backgroundColor: kbForm.allowedAreas.includes(area) ? info.color : 'transparent',
                  color: kbForm.allowedAreas.includes(area) ? 'white' : info.color
                }}>
                  {info.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-2">Selecciona las áreas que podrán acceder a esta información vía Xtreme-AI</p>
          </div>
        </div>
      </Modal>

      {/* Modal Eliminar KB */}
      <Modal isOpen={showDeleteKBModal} onClose={() => setShowDeleteKBModal(false)} title="Eliminar Entrada" onSubmit={(e) => { e.preventDefault(); handleDeleteKB(); }} submitText="Eliminar" submitButtonClass="btn-danger">
        <div className="p-4 bg-red-50 text-red-700 rounded-md border border-red-200">
          <p>¿Eliminar: <strong>{entryToDelete?.title}</strong>?</p>
          <p className="text-sm mt-1">Xtreme-AI dejará de usar esta información en sus respuestas.</p>
        </div>
      </Modal>

      <NotificationSystem notification={notification} onClose={hideNotification} />
    </div>
  );
};

export default SettingsPage;
